import { useState, useCallback, useMemo } from "react";
import { ChevronDown, ChevronRight, Maximize2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FullScreenWrapper } from "./FullScreenWrapper";
import { cn } from "@/lib/utils";

/* ── Types ─────────────────────────────────────────────── */

interface DiffLine {
  type: "add" | "delete" | "context";
  content: string;
}

interface DiffHunk {
  header: string;
  lines: DiffLine[];
}

export interface DiffFile {
  path: string;
  status: "modified" | "added" | "deleted" | "renamed";
  additions: number;
  deletions: number;
  hunks: DiffHunk[];
  binary?: boolean;
}

interface DiffViewerData {
  files: DiffFile[];
  total_files: number;
  total_additions: number;
  total_deletions: number;
  clean?: boolean;
}

interface DiffViewerWidgetProps {
  data: DiffViewerData;
  actions?: Array<{ id: string; label: string; style?: string }>;
  onAction?: (actionId: string) => void;
  embedded?: boolean;
}

/* ── Single file diff ────────────────────────────────── */

function FileDiffSection({
  file,
  defaultExpanded,
}: {
  file: DiffFile;
  defaultExpanded: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div className="border-b border-daydra-border last:border-b-0">
      {/* Sticky file header */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className="sticky top-0 z-10 flex items-center gap-2 w-full px-3 py-1.5 text-left bg-daydra-raised/80 backdrop-blur-sm hover:bg-daydra-raised transition-colors"
      >
        {expanded ? (
          <ChevronDown size={12} className="text-daydra-muted shrink-0" />
        ) : (
          <ChevronRight size={12} className="text-daydra-muted shrink-0" />
        )}
        <span className="flex-1 min-w-0 font-mono text-xs text-daydra-text truncate">
          {file.path}
        </span>
        <span className="text-2xs text-green-400 shrink-0">+{file.additions}</span>
        <span className="text-2xs text-red-400 shrink-0">-{file.deletions}</span>
      </button>

      {/* Diff lines */}
      {expanded && (
        <div className="overflow-x-auto">
          {file.binary ? (
            <p className="px-3 py-2 text-xs text-daydra-muted italic">Binary file</p>
          ) : (
            file.hunks.map((hunk, hi) => (
              <div key={hi} className="font-mono text-xs leading-[1.7]">
                {hunk.lines.map((line, li) => (
                  <div
                    key={li}
                    className={cn(
                      "flex min-w-fit",
                      line.type === "add" && "bg-green-500/10",
                      line.type === "delete" && "bg-red-500/10",
                    )}
                  >
                    <span
                      className={cn(
                        "w-5 shrink-0 text-center select-none",
                        line.type === "add" && "text-green-500/60",
                        line.type === "delete" && "text-red-500/60",
                        line.type === "context" && "text-daydra-faint",
                      )}
                    >
                      {line.type === "add" ? "+" : line.type === "delete" ? "−" : " "}
                    </span>
                    <pre
                      className={cn(
                        "flex-1 pr-3 whitespace-pre",
                        line.type === "add" && "text-green-400",
                        line.type === "delete" && "text-red-400",
                        line.type === "context" && "text-daydra-dim",
                      )}
                    >
                      {line.content || "\n"}
                    </pre>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/* ── Main widget ─────────────────────────────────────── */

export function DiffViewerWidget({
  data,
  actions,
  onAction,
  embedded = false,
}: DiffViewerWidgetProps) {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [collapsedAll, setCollapsedAll] = useState<boolean | null>(null);

  const { files = [], total_files = 0, total_additions = 0, total_deletions = 0 } = data ?? {};

  // Clean working tree — no diff to show
  if (data?.clean) {
    return (
      <div className="max-w-md animate-slide-up rounded-3xl rounded-bl-lg overflow-hidden" style={{ background: "linear-gradient(135deg, rgba(125,92,240,0.09) 0%, rgba(110,78,220,0.04) 100%)" }}>
        <div className="px-4 py-3 flex items-center gap-2.5">
          <div className="flex items-center justify-center h-7 w-7 rounded-lg bg-green-500/10 shrink-0">
            <span className="text-green-400 text-sm">✓</span>
          </div>
          <p className="text-sm text-daydra-dim">No differences — working tree is clean.</p>
        </div>
      </div>
    );
  }

  const defaultExpanded = useMemo(() => {
    if (collapsedAll === true) return false;
    if (collapsedAll === false) return true;
    return files.length <= 5;
  }, [collapsedAll, files.length]);

  const toggleAll = useCallback(() => {
    setCollapsedAll((prev) => (prev === true ? false : true));
  }, []);

  const header = (
    <div className="flex items-center gap-2 px-3 py-2 text-xs flex-wrap">
      <span className="text-daydra-text font-medium mr-auto">
        {total_files} file{total_files !== 1 ? "s" : ""}
      </span>
      <span className="text-green-400">+{total_additions}</span>
      <span className="text-red-400">-{total_deletions}</span>
      {files.length > 1 && (
        <button
          onClick={toggleAll}
          className="text-2xs text-daydra-muted hover:text-daydra-text transition-colors"
        >
          {collapsedAll === true ? "expand" : "collapse"}
        </button>
      )}
      {!embedded && !isFullScreen && (
        <button
          onClick={() => setIsFullScreen(true)}
          className="text-daydra-muted hover:text-daydra-text transition-colors"
          aria-label="Full screen"
        >
          <Maximize2 size={12} />
        </button>
      )}
    </div>
  );

  const fileList = (maxH: string) => (
    <div className={cn("border-t border-daydra-border overflow-y-auto", maxH)}>
      {files.map((file, i) => (
        <FileDiffSection
          key={file.path + i}
          file={file}
          defaultExpanded={defaultExpanded}
        />
      ))}
    </div>
  );

  return (
    <>
      <Card
        variant="default"
        padding="none"
        className={cn(
          "overflow-hidden animate-slide-up",
          embedded ? "border-0 rounded-none" : "max-w-2xl",
        )}
      >
        {header}
        {fileList(embedded ? "max-h-[300px]" : "max-h-[400px] sm:max-h-[500px]")}
        {actions && actions.length > 0 && !embedded && (
          <div className="flex gap-2 px-3 py-2.5 border-t border-daydra-border">
            {actions.map((action) => (
              <Button
                key={action.id}
                variant={action.style === "primary" ? "default" : "secondary"}
                size="sm"
                onClick={() => onAction?.(action.id)}
              >
                {action.label}
              </Button>
            ))}
          </div>
        )}
      </Card>

      <FullScreenWrapper
        open={isFullScreen}
        onClose={() => setIsFullScreen(false)}
        title={`${total_files} file${total_files !== 1 ? "s" : ""} — +${total_additions} -${total_deletions}`}
      >
        <div className="max-w-5xl mx-auto p-4">
          <Card variant="default" padding="none" className="overflow-hidden">
            {header}
            <div className="border-t border-daydra-border">
              {files.map((file, i) => (
                <FileDiffSection
                  key={file.path + i}
                  file={file}
                  defaultExpanded={true}
                />
              ))}
            </div>
          </Card>
        </div>
      </FullScreenWrapper>
    </>
  );
}
